import { Link, useRouterState } from '@tanstack/react-router';
import { Group, NavLink, ScrollArea, Stack } from '@mantine/core';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Grouped, vertically-scrollable navbar (Pattern A — workflow).
 *
 * Each group has a parent <NavLink> with a disclosure caret and a list
 * of child NavLinks. The group containing the current route
 * auto-expands; other groups remember their last user-set state in
 * localStorage so cross-session navigation feels stable.
 *
 * Why a single component instead of one-off shells per scope: vendor +
 * central have the same navigation pattern; differences are just the
 * group definitions. Keeps the AppShell.Navbar bodies lean and means
 * "fix the scrollbar" or "tweak the active-state colour" is one edit
 * for both.
 */

export type NavChild = {
  label: string;
  to: string;
  /// Match any deeper sub-route too — e.g. `/vendor/contacts` should
  /// stay highlighted while sitting on `/vendor/contacts/$uid`.
  matchPrefix?: boolean;
  leftSection?: ReactNode;
  /// Optional right-aligned node (e.g. an unread-count badge).
  rightSection?: ReactNode;
  /// Permission key required to render this child. When omitted, the
  /// child is always visible. The vendor shell filters children by
  /// this and drops empty groups so a section header doesn't sit
  /// alone with no children.
  requires?: string;
  /// VendorAdmin / SuperAdmin only — for sub-features no team-member
  /// permission unlocks (Settings/General etc.).
  adminOnly?: boolean;
};

export type NavGroup = {
  /// Stable id used for the localStorage key — keep it short, snake_case.
  id: string;
  label: string;
  leftSection?: ReactNode;
  /// Optional badge shown on the collapsed group header (e.g. a rolled-up
  /// unread total) so the count stays visible without expanding the group.
  badge?: ReactNode;
  children: NavChild[];
};

export type NavTrailer = {
  label: string;
  to: string;
  leftSection?: ReactNode;
};

const STORAGE_PREFIX = 'leadsync.nav.';

/** Does this child match the path at all (exact or prefix)? */
function childMatches(currentPath: string, child: NavChild): boolean {
  if (currentPath === child.to) return true;
  if (child.matchPrefix && currentPath.startsWith(child.to + '/')) return true;
  return false;
}

/** Pick the single most-specific active child within a group. Without this,
 *  a parent like `/vendor/campaigns` (matchPrefix=true) would also light up
 *  when a sibling like `/vendor/campaigns/suppression` is the actual route. */
function activeChildIndex(currentPath: string, group: NavGroup): number {
  let bestIdx = -1;
  let bestLen = -1;
  group.children.forEach((c, i) => {
    if (!childMatches(currentPath, c)) return;
    if (c.to.length > bestLen) {
      bestLen = c.to.length;
      bestIdx = i;
    }
  });
  return bestIdx;
}

function isActiveChild(currentPath: string, child: NavChild, group: NavGroup): boolean {
  const idx = activeChildIndex(currentPath, group);
  return idx >= 0 && group.children[idx] === child;
}

function isActiveGroup(currentPath: string, group: NavGroup): boolean {
  return activeChildIndex(currentPath, group) >= 0;
}

function loadOpenState(scope: string, groupId: string): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(`${STORAGE_PREFIX}${scope}.${groupId}`);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

function saveOpenState(scope: string, groupId: string, open: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${scope}.${groupId}`, open ? '1' : '0');
  } catch {
    // localStorage disabled / quota exceeded — fall through silently.
  }
}

export function GroupedNav(props: { scope: string; groups: NavGroup[]; trailer?: NavTrailer }) {
  const { location } = useRouterState();
  const currentPath = location.pathname;

  const initialOpen = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const g of props.groups) {
      const persisted = loadOpenState(props.scope, g.id);
      // Persisted wins; otherwise auto-expand only the active group so
      // the navbar stays short for first-time visitors.
      out[g.id] = persisted ?? isActiveGroup(currentPath, g);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.scope]);

  const [openById, setOpenById] = useState<Record<string, boolean>>(initialOpen);

  // When the route changes into a different group's child, force-open
  // that group too — the user just navigated there, expanding makes
  // sure the new active item is visible without an extra click.
  useEffect(() => {
    setOpenById((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of props.groups) {
        if (isActiveGroup(currentPath, g) && !next[g.id]) {
          next[g.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const toggle = (groupId: string) => {
    setOpenById((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      saveOpenState(props.scope, groupId, next[groupId] ?? false);
      return next;
    });
  };

  return (
    <Stack gap={0} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ScrollArea type="hover" scrollbarSize={6} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap={2} p="sm">
          {props.groups.map((g) => {
            // Auto-flatten single-child groups: render the child as a
            // top-level leaf instead of a disclosable parent. Saves a
            // wasted click for nav like Dashboard / Billing where the
            // group label and the only child are effectively the same.
            if (g.children.length === 1) {
              const only = g.children[0]!;
              const onlyActive = isActiveChild(currentPath, only, g);
              return (
                <NavLink
                  key={g.id}
                  component={Link}
                  to={only.to}
                  label={g.label}
                  leftSection={g.leftSection ?? only.leftSection}
                  active={onlyActive}
                  variant={onlyActive ? 'light' : 'subtle'}
                />
              );
            }
            const open = openById[g.id] ?? false;
            const groupActive = isActiveGroup(currentPath, g);
            return (
              <NavLink
                key={g.id}
                label={
                  !open && g.badge ? (
                    <Group gap="xs" wrap="nowrap">
                      <span>{g.label}</span>
                      {g.badge}
                    </Group>
                  ) : (
                    g.label
                  )
                }
                leftSection={g.leftSection}
                opened={open}
                onClick={(e) => {
                  e.preventDefault();
                  toggle(g.id);
                }}
                active={groupActive}
                variant={groupActive ? 'light' : 'subtle'}
                childrenOffset={28}
              >
                {g.children.map((c) => (
                  <NavLink
                    key={c.to}
                    component={Link}
                    to={c.to}
                    label={c.label}
                    leftSection={c.leftSection}
                    rightSection={c.rightSection}
                    active={isActiveChild(currentPath, c, g)}
                  />
                ))}
              </NavLink>
            );
          })}
        </Stack>
      </ScrollArea>
      {props.trailer && (
        <Stack gap={0} p="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <NavLink
            component={Link}
            to={props.trailer.to}
            label={props.trailer.label}
            leftSection={props.trailer.leftSection}
          />
        </Stack>
      )}
    </Stack>
  );
}
